/**
 * Persists the most recent self-debate batch preflight attempt so users
 * see the same retry context after refreshing Oratory Studio or the
 * landing-page Self-Debate Studio preview.
 *
 * Best-effort localStorage write; never throws if quota / privacy mode
 * blocks access. We deliberately do NOT persist the raw file blobs —
 * only the metadata needed to reconstruct the retry UI state.
 */
import type { CaptureKind } from "@/lib/selfDebatePreflight";

export type RehearsalMode = "home" | "office";
export type Template = "opening" | "rebuttal" | "closing";

export interface LastBatchAttempt {
  mode: RehearsalMode;
  template: Template;
  /** "initial" = first run for this batch; "retry" = re-validation pass. */
  lastAction: "initial" | "retry";
  /** 1-based attempt counter. */
  attempt: number;
  /** Per-file metadata (kind + label + size) so the UI can re-show context. */
  files: Array<{ kind: CaptureKind; label: string; sizeBytes: number }>;
  /** Whether the failed-file details drawer was open when last persisted. */
  failedDetailsOpen?: boolean;
  /** ISO timestamp the attempt was last recorded. */
  savedAt: string;
}

const STORAGE_KEY = "thefloor.lastBatchAttempt.v1";

const MODES: RehearsalMode[] = ["home", "office"];
const TEMPLATES: Template[] = ["opening", "rebuttal", "closing"];
const KINDS: CaptureKind[] = ["text", "audio", "video"];

export function saveLastBatchAttempt(attempt: Omit<LastBatchAttempt, "savedAt">): void {
  try {
    const payload: LastBatchAttempt = { ...attempt, savedAt: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* best-effort */
  }
}

export function loadLastBatchAttempt(): LastBatchAttempt | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LastBatchAttempt;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      !MODES.includes(parsed.mode) ||
      !TEMPLATES.includes(parsed.template) ||
      !["initial", "retry"].includes(parsed.lastAction) ||
      !Array.isArray(parsed.files)
    ) {
      return null;
    }
    // Sanitise files; drop entries with unknown kinds.
    parsed.files = parsed.files.filter(
      (f) => f && KINDS.includes(f.kind) && typeof f.sizeBytes === "number" && typeof f.label === "string",
    );
    return parsed;
  } catch {
    return null;
  }
}

export function clearLastBatchAttempt(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
